import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, QrCode } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { api, getMe } from "../lib/api";
import GuestGate from "../components/GuestGate";
import { useI18n } from "../lib/i18n";
import { ModeToggleRow } from "../components/ProfileDropdown";

/**
 * /my-qr — the user's personal sharable QR. For customers (not signed in),
 * show the friendly GuestGate prompt instead of attempting an authed
 * fetch that would 401 and leave the user staring at an error.
 */
export default function MyQr() {
  const { t } = useI18n();
  const [authState, setAuthState] = useState("loading"); // loading | customer | authed
  const [qr, setQr] = useState(null);
  const [gateOpen, setGateOpen] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const me = await getMe();
      if (cancelled) return;
      if (!me) { setAuthState("guest"); return; }
      setAuthState("authed");
      try {
        const { data } = await api.get("/me/qr");
        if (!cancelled) setQr(data);
      } catch {
        // ignored — UI will simply not render the QR
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (authState === "loading") {
    return (
      <main className="min-h-screen grid place-items-center" style={{ background: "#171717" }}>
        <div className="font-display text-2xl text-gold animate-pulse">Habano</div>
      </main>
    );
  }

  if (authState === "guest") {
    return (
      <main
        data-testid="my-qr-guest"
        className="min-h-screen bg-background grid place-items-center px-6 relative"
      >
        <Link to="/" className="absolute top-4 left-4 btn-ghost p-2" data-testid="my-qr-back">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5}/>
        </Link>
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 mx-auto grid place-items-center rounded-full border border-gold-strong bg-[rgba(232,184,75,0.06)]">
            <QrCode className="w-7 h-7 text-gold" strokeWidth={1.5} />
          </div>
          <div className="eyebrow mt-5 text-gold">{t("qr_personal_eyebrow")}</div>
          <h1 className="font-display text-3xl mt-2 text-foreground leading-tight">
            {t("qr_personal_title_signin")}
          </h1>
          <p className="mt-4 text-base text-foreground/65 leading-relaxed">
            {t("qr_personal_body")}
          </p>
          <button
            data-testid="my-qr-cta"
            type="button"
            onClick={() => setGateOpen(true)}
            className="mt-7 inline-flex items-center justify-center gap-2 px-6 py-3 bg-gold text-[hsl(var(--primary-foreground))] hover:opacity-90 text-xs uppercase tracking-widest"
            style={{ minHeight: 48 }}
          >
            {t("qr_personal_cta")}
          </button>
        </div>

        <GuestGate
          open={gateOpen}
          onOpenChange={setGateOpen}
          title={t("qr_personal_eyebrow")}
          message={t("qr_personal_body")}
          primaryAction="signin"
        />
      </main>
    );
  }

  return (
    <main data-testid="my-qr-page" className="min-h-screen bg-background grid place-items-center px-6 relative">
      <Link to="/friends" className="absolute top-4 left-4 btn-ghost p-2"><ArrowLeft className="w-4 h-4" strokeWidth={1.5}/></Link>
      <div className="text-center">
        <div className="eyebrow">{t("qr_share_eyebrow")}</div>
        <h1 className="font-display text-3xl mt-2 text-foreground">{t("qr_share_title")}</h1>
        <div className="mt-8 inline-block p-6 bg-foreground rounded-[20px]">
          {qr ? <QRCodeSVG value={qr.qr_data} size={260} level="M" fgColor="#0B0B0B" bgColor="#F5F1E8" /> : null}
        </div>
        <div className="mt-4 text-[11px] font-mono text-foreground/45" data-testid="my-qr-data">{qr?.qr_data}</div>
        <p className="mt-6 text-sm text-foreground/55 max-w-xs mx-auto">{t("qr_share_body")}</p>

        {/* iter169 — Ride/Discover toggle reused from ProfileDropdown so the
         * user can switch app mode without backing out of this screen. */}
        <div data-testid="my-qr-mode-toggle" className="mt-8 max-w-xs mx-auto">
          <ModeToggleRow />
        </div>
      </div>
    </main>
  );
}
